import { canonicalCrowdexAcpAgentId } from '@crowdex/agent-runtime/identity';
import type { PromptInputMessage } from '@crowdex/ui/ai/prompt-input';
import type { ChatRequestOptions } from 'ai';
import { type RefObject, useCallback, useEffect, useRef } from 'react';
import type { SidePanelView } from '@/components/desktop-side-panel';
import type { RemoteExecutionTarget } from '@/components/worktree-controls';
import { CORE_TERMINAL_VIEW } from '@/features/plugins/core-panels';
import { placeNewConversation } from '@/lib/cloud-link';
import {
  type CloudRuntime,
  fetchCloudRuntime,
  handoffCloudTaskProvider,
} from '@/lib/cloud-runtime/cloud-runtime';
import { agentCommandName } from '@/lib/desktop-shell/cloud-agents';
import type { ConversationScope, ConversationScopes } from '@/lib/desktop-shell/conversation-scope';
import { loadExecutionTargets } from '@/lib/desktop-shell/execution-targets';
import { conversationStateOf, type DesktopShellStore } from '@/lib/desktop-shell/store';
import { errorMessage } from '@/lib/error-message';

/** Why a cloud conversation could not start: the server has no cloud sandbox to run it in. */
export const NO_CLOUD_RUNTIME_MESSAGE = 'No cloud runtime is configured.';

export type CloudRuntimeControls = {
  activateCloudRuntime: () => Promise<CloudRuntime | null>;
  handoffRemoteProvider: (target: RemoteExecutionTarget) => Promise<void>;
  /** Sends the message; `false` when its cloud sandbox could not start (nothing was sent). */
  submitMessage: (message: PromptInputMessage, options?: ChatRequestOptions) => Promise<boolean>;
};

function isCloudWorkspaceView(view: SidePanelView | null): boolean {
  return (
    view === 'review' ||
    view === CORE_TERMINAL_VIEW ||
    view === 'files' ||
    view === 'results' ||
    view?.startsWith('artifact:') === true
  );
}

/**
 * Owns the cloud runtime lifecycle of each conversation: lazily provisions and fetches its
 * runtime (one start at a time per conversation), routes a message through that start when the
 * cloud agent is selected but disconnected, and auto-activates when a cloud-only side panel is
 * opened. A start takes tens of seconds: the runtime it brings belongs to the conversation it
 * started for, shown when that conversation is again, and its waiting message leaves there.
 */
export function useCloudRuntime({
  store,
  conversations,
  preview,
  agentCommand,
  chatId,
  cloudAgentSelected,
  cloudRuntime,
  sidePanelOpen,
  sidePanelView,
  sendMessage,
  cloudRuntimeRef,
  cloudDisconnectedRef,
  provisionCloudWorkspaceRef,
}: {
  store: DesktopShellStore;
  conversations: ConversationScopes;
  preview: boolean;
  agentCommand: string | undefined;
  chatId: string;
  cloudAgentSelected: boolean;
  cloudRuntime: CloudRuntime | null;
  sidePanelOpen: boolean;
  sidePanelView: SidePanelView | null;
  sendMessage: (message: PromptInputMessage, options?: ChatRequestOptions) => Promise<void>;
  cloudRuntimeRef: RefObject<CloudRuntime | null>;
  cloudDisconnectedRef: RefObject<boolean>;
  provisionCloudWorkspaceRef: RefObject<() => Promise<boolean>>;
}): CloudRuntimeControls {
  // Each conversation's start in flight, and the runtime each one got.
  const activationsRef = useRef(new Map<string, Promise<CloudRuntime | null>>());
  const runtimesRef = useRef(new Map<string, CloudRuntime>());

  // A conversation shown again connects to its runtime when it needs it; one whose message waits
  // for the runtime it started while the user was elsewhere has it at once. What was said about
  // another conversation's start is not about this one.
  useEffect(() => {
    if (preview) return;
    const state = store.getState();
    const started = runtimesRef.current.get(chatId);
    runtimesRef.current.delete(chatId);
    state.setCloudRuntime(
      conversationStateOf(state, chatId).pendingSend?.kind === 'cloud' ? (started ?? null) : null,
    );
    state.setCloudProvisionError(null);
    state.setMessageNotSent(null);
  }, [chatId, preview, store]);

  const activateCloudRuntime = useCallback(async (): Promise<CloudRuntime | null> => {
    const conversation = conversations.of(chatId);
    if (cloudRuntimeRef.current !== null && !cloudDisconnectedRef.current) {
      return cloudRuntimeRef.current;
    }
    const inFlight = activationsRef.current.get(chatId);
    if (inFlight !== undefined) return inFlight;
    const { setCloudProvisionError, setCloudProvisioningPhase, setDiscoveringAgents } =
      store.getState();
    setCloudProvisionError(null);
    setCloudProvisioningPhase('task-configuration');
    setDiscoveringAgents(true);
    // This conversation's provisioning, as it is now: after an await the ref holds the next one's.
    const provision = provisionCloudWorkspaceRef.current;
    const activation = (async (): Promise<CloudRuntime | null> => {
      const selectedTarget = await placeOnItsHost(store, conversation);
      const provisioned = await provision();
      if (!provisioned) return null;
      const runtime = await fetchCloudRuntime(
        chatId,
        canonicalCrowdexAcpAgentId(agentCommandName(agentCommand ?? 'codex-acp')),
        (phase) => conversation.whileShown((state) => state.setCloudProvisioningPhase(phase)),
        selectedTarget === null
          ? undefined
          : {
              providerId: selectedTarget.providerId,
              profileId: selectedTarget.profileId,
            },
      );
      if (runtime === null) throw new Error(NO_CLOUD_RUNTIME_MESSAGE);
      runtimesRef.current.set(chatId, runtime);
      conversation.whileShown((state) => {
        cloudRuntimeRef.current = runtime;
        cloudDisconnectedRef.current = false;
        state.setCloudRuntime(runtime);
        state.setRemoteExecutionTarget({
          cloud: selectedTarget?.cloud ?? false,
          providerId: runtime.providerId,
          profileId: runtime.profileId,
          label: runtime.name,
        });
      });
      conversation.place({ workspace: runtime.workspaceRoot });
      // Its waiting message can leave now, shown or not (`usePendingMessageDrain`).
      conversation.update(({ pendingSend }) =>
        pendingSend?.kind === 'cloud' ? { pendingSend: { ...pendingSend, ready: true } } : {},
      );
      return runtime;
    })()
      .catch((error: unknown) => {
        conversation.whileShown((state) => state.setCloudProvisionError(errorMessage(error)));
        return null;
      })
      .finally(() => {
        activationsRef.current.delete(chatId);
        // Still discovering while another conversation's start runs.
        store.getState().setDiscoveringAgents(activationsRef.current.size > 0);
      });
    activationsRef.current.set(chatId, activation);
    return activation;
  }, [
    store,
    conversations,
    agentCommand,
    chatId,
    cloudRuntimeRef,
    cloudDisconnectedRef,
    provisionCloudWorkspaceRef,
  ]);

  const handoffRemoteProvider = useCallback(
    async (target: RemoteExecutionTarget): Promise<void> => {
      const conversation = conversations.of(chatId);
      try {
        const moved = await handoffCloudTaskProvider(chatId, target);
        // The conversation follows its host, on this server or the cloud.
        store.getState().setSyncedThreads((current) =>
          current.map((entry) => {
            if (entry.id !== chatId) return entry;
            const { onCloud: _cloud, ...rest } = entry;
            return moved.cloud ? { ...rest, onCloud: true as const } : rest;
          }),
        );
        runtimesRef.current.delete(chatId);
        // Shown elsewhere meanwhile: it starts on its new host when it is shown again.
        const shown = conversation.whileShown((state) => {
          cloudRuntimeRef.current = null;
          cloudDisconnectedRef.current = true;
          state.setCloudRuntime(null);
          state.setRemoteExecutionTarget(target);
        });
        if (shown) await activateCloudRuntime();
      } catch (error) {
        conversation.whileShown((state) => state.setCloudProvisionError(errorMessage(error)));
      }
    },
    [activateCloudRuntime, chatId, cloudDisconnectedRef, cloudRuntimeRef, conversations, store],
  );

  const submitMessage = useCallback(
    async (message: PromptInputMessage, options?: ChatRequestOptions): Promise<boolean> => {
      store.getState().setMessageNotSent(null);
      if (
        !preview &&
        cloudAgentSelected &&
        (cloudRuntime === null || cloudDisconnectedRef.current)
      ) {
        const conversation = conversations.of(chatId);
        // It leaves once this conversation's sandbox is ready (`usePendingMessageDrain`).
        conversation.update({
          pendingSend: {
            kind: 'cloud',
            message,
            ...(options === undefined ? {} : { options }),
            ready: false,
          },
        });
        cloudRuntimeRef.current = null;
        runtimesRef.current.delete(chatId);
        store.getState().setCloudRuntime(null);
        const runtime = await activateCloudRuntime();
        if (runtime === null) {
          conversation.update({ pendingSend: null });
          // Nothing left: the conversation says why, the composer has the message back.
          conversation.whileShown((state) =>
            state.setMessageNotSent(
              state.cloudProvisionError ?? 'The cloud agent could not start.',
            ),
          );
          return false;
        }
        return true;
      }
      if (options === undefined) await sendMessage(message);
      else await sendMessage(message, options);
      return true;
    },
    [
      store,
      conversations,
      chatId,
      activateCloudRuntime,
      cloudAgentSelected,
      cloudRuntime,
      preview,
      sendMessage,
      cloudRuntimeRef,
      cloudDisconnectedRef,
    ],
  );

  useEffect(() => {
    if (
      preview ||
      !cloudAgentSelected ||
      cloudRuntime !== null ||
      !sidePanelOpen ||
      !isCloudWorkspaceView(sidePanelView)
    ) {
      return;
    }
    void activateCloudRuntime();
  }, [
    activateCloudRuntime,
    cloudAgentSelected,
    cloudRuntime,
    preview,
    sidePanelOpen,
    sidePanelView,
  ]);

  return { activateCloudRuntime, handoffRemoteProvider, submitMessage };
}

/**
 * A new conversation can start in Cloud mode without the mode being chosen for it (a new chat
 * keeps the current one): before its runtime is asked for, it runs on a remote host (the first
 * one, this server's then the cloud's), and lives on the cloud when that host is the cloud's.
 * A conversation this server already saved keeps its place: moving it is a handoff. Says the
 * host the conversation's runtime is asked from.
 */
export async function placeOnItsHost(
  store: DesktopShellStore,
  conversation: ConversationScope,
): Promise<RemoteExecutionTarget | null> {
  const { syncedThreads, remoteExecutionTarget } = store.getState();
  const saved = syncedThreads.find((thread) => thread.id === conversation.chatId);
  if (saved !== undefined && saved.onCloud !== true) return remoteExecutionTarget;
  const host = remoteExecutionTarget ?? (await loadExecutionTargets())[0] ?? null;
  if (host === null) return null;
  if (remoteExecutionTarget === null)
    conversation.whileShown((state) => state.setRemoteExecutionTarget(host));
  if (host.cloud) await placeNewConversation(conversation.chatId, true);
  return host;
}
